import { PublicClientApplication, Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Configuracion MSAL (Microsoft Authentication Library) para el flujo de login
 * con Azure AD. Se usa como alternativa a Firebase para Microsoft, igualando
 * la implementacion de V2 (V2/tufirma-backoffice/src/app/app.module.ts:42-50).
 *
 * Variables de entorno requeridas:
 *   - NEXT_PUBLIC_AZURE_AD_CLIENT_ID: App ID registrada en Azure Portal
 *   - NEXT_PUBLIC_AZURE_AD_TENANT:     'common' | 'organizations' | '<tenant-id>'
 *
 * El backend valida el idToken devuelto por MSAL verificando la firma contra
 * las JWKS publicas de Microsoft (ver `ms-users-auth/src/features/auth/services/oauth.service.ts`).
 */
const clientId = process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID || '';
const tenant = process.env.NEXT_PUBLIC_AZURE_AD_TENANT || 'common';

export const msalConfig: Configuration = {
  auth: {
    clientId,
    // Authority `/v2.0` fuerza a Azure AD a emitir tokens en formato v2.0
    // (issuer = `https://login.microsoftonline.com/{tid}/v2.0`). Sin esto,
    // Azure puede emitir tokens v1.0 con issuer `https://sts.windows.net/{tid}/`,
    // que el backend (`ms-users-auth/oauth.service.ts:_validateAzureAdToken`)
    // rechaza porque solo acepta el issuer v2.0.
    authority: `https://login.microsoftonline.com/${tenant}/v2.0`,
    // Redirect URI para el callback MSAL. Debe estar registrado en Azure Portal
    // bajo "Authentication > Redirect URIs" del app registration.
    redirectUri: typeof window !== 'undefined' ? `${window.location.origin}/auth/redirect` : 'http://localhost:3010/auth/redirect',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (): void => {
        /* silent */
      },
      logLevel: LogLevel.Error,
    },
  },
};

/**
 * Scopes solicitados durante el login. `openid` + `profile` + `email` dan
 * acceso al idToken con la informacion basica del usuario (email, nombre).
 * `User.Read` es el scope minimo de MS Graph (no se usa por ahora, pero
 * permite hacer llamadas a Graph API si se necesita luego).
 */
export const loginRequestScopes = ['openid', 'profile', 'email', 'User.Read'];

// Singleton MSAL: se crea una sola instancia por sesion del navegador.
// Se instancia lazy la primera vez que se llama `getMsalInstance()` para
// evitar errores en SSR (Next.js ejecuta el import tambien en el server).
let _instance: PublicClientApplication | null = null;

export const getMsalInstance = (): PublicClientApplication => {
  if (typeof window === 'undefined') {
    throw new Error('MSAL no disponible en SSR, debe llamarse desde el cliente');
  }
  if (!clientId) {
    throw new Error('NEXT_PUBLIC_AZURE_AD_CLIENT_ID no definido. Revisa .env.local');
  }
  if (!_instance) {
    _instance = new PublicClientApplication(msalConfig);
  }
  return _instance;
};
